import {memo, useState, useEffect, useMemo, useCallback, useRef} from 'react';
import {
  Palette,
  InvitedUser,
  InvitedUserFields,
  sanitizeString,
  TranslatedLanguage,
  User,
} from 'hubbl-shared';
import {
  WhiteContainer,
  TextHeading3,
  TextHeading5,
  TextBodyMedium,
  TextBodySmall,
  PaddingBottom,
  UserListModal,
} from '../index';
import {
  useAppSelector,
  useAppDispatch,
  useI18n,
  useEmployeesSync,
} from '../../hooks';
import {UserSelector} from '../../store/user/UserSelector';
import {GlobalActions} from '../../store/global/GlobalActions';
import {Api} from '../../api';
import styled from 'styled-components';
import {AnimatePresence, motion} from 'framer-motion';
import {PrimaryButton} from '../buttons/primary-button/PrimaryButton';
import {SecondaryButton} from '../buttons/secondary-button/SecondaryButton';
import {CloseButton} from '../buttons/close-button/CloseButton';
import {IconButton} from '../buttons/icon-button/IconButton';
import {InviteUsersModal} from '../InviteUsers/InviteUsersModal';
import {AssetIconUpload, AssetIconTrash} from '../../assets';
import {AnimatedProgressBar} from './AnimatedProgressBar';

interface OnboardingData {
  activeUsers: number;
  pendingInvites: number;
  totalExpected: number;
  onboardingPercentage: number;
}

const ProgressContainer = styled.div`
  width: 100%;
  margin-bottom: 16px;
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 16px;
`;

const StatItem = styled.div`
  text-align: center;
  padding: 12px;
  background: ${Palette.primarySubtleGreen};
  border-radius: 8px;
`;

const LoadingContainer = styled.div`
  padding: 40px;
  text-align: center;
  color: ${Palette.almostBlack500};
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const hasRequiredInviteMetadata = (invitedUser: InvitedUser): boolean => {
  const hasFullName = Boolean(invitedUser.fullName?.trim());
  const hasJobTitle = Boolean(invitedUser.jobTitle?.trim());
  const hasDepartment = Boolean(invitedUser.department);

  return hasFullName && hasJobTitle && hasDepartment;
};

const invitedUserToPayload = (invitedUser: InvitedUser): InvitedUserFields => {
  const fallbackLabel = 'General';
  return {
    email: invitedUser.email,
    companyId: invitedUser.companyId,
    companyName: invitedUser.companyName,
    fullName: invitedUser.fullName || invitedUser.email,
    jobTitle: invitedUser.jobTitle || '',
    department: invitedUser.department || {
      id: sanitizeString(fallbackLabel),
      label: {
        [TranslatedLanguage.English]: fallbackLabel,
        [TranslatedLanguage.Danish]: fallbackLabel,
      },
    },
  };
};

// Modal styles
const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled(motion.div)`
  background: ${Palette.white};
  border-radius: 16px;
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  position: relative;
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  padding: 16px;
  border-bottom: 1px solid ${Palette.almostBlackTransparent5};
`;

const ModalBody = styled.div`
  padding: 16px;
  overflow-y: auto;
  flex: 1;
`;

const ModalFooter = styled.div`
  padding: 16px;
  border-top: 1px solid ${Palette.almostBlackTransparent5};
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;

const InviteItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  margin-bottom: 8px;
  background: ${Palette.primarySubtleGreen};
  border-radius: 8px;
  border: 1px solid ${Palette.almostBlackTransparent10};
`;

const InviteItemActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ClickableStatItem = styled(StatItem)<{clickable?: boolean}>`
  cursor: ${props => (props.clickable ? 'pointer' : 'default')};
  transition: all 0.2s ease;

  &:hover {
    ${props =>
      props.clickable &&
      `
      transform: translateY(-1px);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
      outline: 1px solid ${Palette.primaryTerraCotta};
    `}
  }
`;

const ClickableProgressContainer = styled(ProgressContainer)<{
  clickable?: boolean;
}>`
  cursor: ${props => (props.clickable ? 'pointer' : 'default')};
`;

export const EmployeeOnboardingProgress = memo(() => {
  const userDetails = useAppSelector(UserSelector.getUserDetails);
  const dispatch = useAppDispatch();
  const t = useI18n();
  const {employees} = useEmployeesSync();
  const [onboardingData, setOnboardingData] = useState<OnboardingData | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showPendingModal, setShowPendingModal] = useState(false);
  const [pendingUsers, setPendingUsers] = useState<InvitedUser[]>([]);
  const [loadingPendingUsers, setLoadingPendingUsers] = useState(false);
  const [reInviteLoading, setReInviteLoading] = useState<string | null>(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showCalendarModal, setShowCalendarModal] = useState(false);
  const prevShowInviteModalRef = useRef(false);

  const loadOnboardingData = useCallback(async () => {
    if (!userDetails?.companyId) return;

    try {
      setLoading(true);
      setError(null);

      const [activeUsers, pendingInvites] = await Promise.all([
        Api.user.getUsersCount(userDetails.companyId),
        Api.invitedUsers.getPendingInvitedUsersCount(userDetails.companyId),
      ]);

      const totalExpected = activeUsers + pendingInvites;
      const onboardingPercentage =
        totalExpected > 0 ? (activeUsers / totalExpected) * 100 : 0;

      setOnboardingData({
        activeUsers,
        pendingInvites,
        totalExpected,
        onboardingPercentage,
      });
    } catch (err) {
      setError(t.common.failedToLoadOnboardingData);
      console.error('Error loading onboarding data:', err);
    } finally {
      setLoading(false);
    }
  }, [t.common.failedToLoadOnboardingData, userDetails?.companyId]);

  useEffect(() => {
    loadOnboardingData();
  }, [loadOnboardingData]);

  // Refresh onboarding data when invite modal closes (transitions from open to closed)
  useEffect(() => {
    if (
      prevShowInviteModalRef.current &&
      !showInviteModal &&
      userDetails?.companyId
    ) {
      loadOnboardingData();
    }
    prevShowInviteModalRef.current = showInviteModal;
  }, [showInviteModal, userDetails?.companyId, loadOnboardingData]);

  // Handler to fetch and show pending users
  const handleShowPendingUsers = useCallback(async () => {
    if (!userDetails?.companyId || onboardingData?.pendingInvites === 0) return;

    setLoadingPendingUsers(true);
    try {
      const users = await Api.invitedUsers.getInvitedUsers(
        userDetails.companyId,
      );
      const pendingOnly = users.filter(user => user.status === 'pending');
      setPendingUsers(pendingOnly);
      setShowPendingModal(true);
    } catch (err) {
      console.error('Error fetching pending users:', err);
    } finally {
      setLoadingPendingUsers(false);
    }
  }, [userDetails?.companyId, onboardingData?.pendingInvites]);

  // Handler to re-invite a specific user
  const handleReInvite = useCallback(
    async (invitedUser: InvitedUser) => {
      if (!userDetails?.companyId) return;

      setReInviteLoading(invitedUser.email);
      try {
        dispatch(GlobalActions.setIsBusy(true));
        if (hasRequiredInviteMetadata(invitedUser)) {
          await Api.invitedUsers.onInviteUsersByEmail({
            companyId: userDetails.companyId,
            invitedUsers: [invitedUserToPayload(invitedUser)],
          });
        } else {
          await Api.invitedUsers.reinvitePendingUser(invitedUser.email);
        }

        // Refresh the pending users list
        const users = await Api.invitedUsers.getInvitedUsers(
          userDetails.companyId,
        );
        const pendingOnly = users.filter(user => user.status === 'pending');
        setPendingUsers(pendingOnly);
      } catch (err) {
        console.error('Error re-inviting user:', err);
      } finally {
        setReInviteLoading(null);
        dispatch(GlobalActions.setIsBusy(false));
      }
    },
    [userDetails?.companyId, dispatch],
  );

  // Handler to delete a pending invite
  const handleDeleteInvite = useCallback(
    async (email: string) => {
      if (!userDetails?.companyId) return;

      try {
        dispatch(GlobalActions.setIsBusy(true));
        await Api.invitedUsers.deleteInvitedUser(userDetails.companyId, email);

        // Refresh the pending users list and onboarding data
        const users = await Api.invitedUsers.getInvitedUsers(
          userDetails.companyId,
        );
        const pendingOnly = users.filter(user => user.status === 'pending');
        setPendingUsers(pendingOnly);
        loadOnboardingData();
      } catch (err) {
        console.error('Error deleting invited user:', err);
      } finally {
        dispatch(GlobalActions.setIsBusy(false));
      }
    },
    [userDetails?.companyId, dispatch, loadOnboardingData],
  );

  const statusMessage = useMemo(() => {
    if (!onboardingData) return '';

    const {onboardingPercentage, pendingInvites} = onboardingData;

    if (onboardingPercentage === 100) {
      return t.common.allTeamMembersJoined;
    } else if (onboardingPercentage >= 80) {
      return t.common.almostThereMostTeamOnboard;
    } else if (onboardingPercentage >= 50) {
      return t.common.goodProgressMoreThanHalf;
    } else if (pendingInvites > 0) {
      return t.common.teamMembersStillJoining;
    } else {
      return t.common.gettingStartedTeamGrowing;
    }
  }, [onboardingData, t]);

  const calendarIntegrationData = useMemo(() => {
    if (!employees || employees.length === 0) {
      return {
        connected: 0,
        notConnected: 0,
        total: 0,
        percentage: 0,
      };
    }

    const connected = employees.filter(
      emp => !!emp?.settings?.calendarIntegration,
    ).length;
    const total = employees.length;
    const notConnected = total - connected;
    const percentage = total > 0 ? (connected / total) * 100 : 0;

    return {
      connected,
      notConnected,
      total,
      percentage,
    };
  }, [employees]);

  const handleShowCalendarIntegration = useCallback(() => {
    if (employees && employees.length > 0) {
      setShowCalendarModal(true);
    }
  }, [employees]);

  if (loading) {
    return (
      <>
        <SectionHeader>
          <TextHeading3 text={t.common.teamOnboarding} />
        </SectionHeader>
        <WhiteContainer>
          <LoadingContainer>
            {t.common.loadingOnboardingProgress}
          </LoadingContainer>
        </WhiteContainer>
      </>
    );
  }

  if (error || !onboardingData) {
    return (
      <>
        <SectionHeader>
          <TextHeading3 text={t.common.teamOnboarding} />
        </SectionHeader>
        <WhiteContainer>
          <LoadingContainer>
            {error || t.common.unableToLoadOnboardingData}
          </LoadingContainer>
        </WhiteContainer>
      </>
    );
  }

  const {activeUsers, pendingInvites, totalExpected, onboardingPercentage} =
    onboardingData;

  return (
    <>
      <SectionHeader>
        <TextHeading3 text={t.common.teamOnboarding} />
        {userDetails?.companyId && (
          <IconButton
            backgroundColor={Palette.primaryBianca}
            text={t.scenes.createHubbl.inviteColleagues}
            icon={<AssetIconUpload />}
            onClick={() => setShowInviteModal(true)}
          />
        )}
      </SectionHeader>
      <WhiteContainer>
        <TextHeading5
          text={`${activeUsers} of ${totalExpected} ${t.common.teamMembersActive}`}
        />
        <PaddingBottom amount={8} />

        <ProgressContainer>
          <AnimatedProgressBar
            percentage={onboardingPercentage}
            duration={1500}
          />
          <TextBodyMedium text={statusMessage} color={Palette.almostBlack} />
        </ProgressContainer>

        <StatsContainer>
          <StatItem>
            <TextHeading5
              text={activeUsers.toString()}
              color={Palette.primaryTerraCotta}
            />
            <TextBodySmall
              text={t.common.activeMembers}
              color={Palette.almostBlack500}
            />
          </StatItem>

          <ClickableStatItem
            clickable={pendingInvites > 0}
            onClick={pendingInvites > 0 ? handleShowPendingUsers : undefined}>
            <TextHeading5
              text={pendingInvites.toString()}
              color={Palette.primaryCherokee}
            />
            <TextBodySmall
              text={
                pendingInvites > 0
                  ? t.common.pendingInvitesClickToView
                  : t.common.pendingInvites
              }
              color={Palette.almostBlack500}
            />
          </ClickableStatItem>
        </StatsContainer>

        <TextBodySmall
          text={t.common.trackTeamOnboardingProgress}
          color={Palette.almostBlack500}
        />

        {calendarIntegrationData.total > 0 && (
          <>
            <PaddingBottom amount={16} />
            <ClickableProgressContainer
              clickable={true}
              onClick={handleShowCalendarIntegration}>
              <TextHeading5
                text={`${calendarIntegrationData.connected} of ${calendarIntegrationData.total} ${t.calendar.calendarIntegration}`}
              />
              <PaddingBottom amount={8} />
              <AnimatedProgressBar
                percentage={calendarIntegrationData.percentage}
                duration={1500}
              />
              <TextBodySmall
                text={t.calendar.calendarIntegrationProgressDescription}
                color={Palette.almostBlack500}
              />
            </ClickableProgressContainer>
          </>
        )}
      </WhiteContainer>

      {/* Pending Users Modal */}
      <AnimatePresence>
        {showPendingModal && (
          <ModalOverlay
            onClick={() => setShowPendingModal(false)}
            initial={{opacity: 0}}
            animate={{opacity: 1}}
            exit={{opacity: 0}}
            transition={{duration: 0.2, ease: 'easeInOut'}}>
            <ModalContent
              initial={{y: 20, opacity: 0}}
              animate={{y: 0, opacity: 1}}
              exit={{y: -20, opacity: 0}}
              transition={{duration: 0.2, ease: 'easeInOut'}}
              onClick={e => e.stopPropagation()}>
              <CloseButton onClick={() => setShowPendingModal(false)} />

              <ModalHeader>
                <TextHeading3
                  text={`${t.common.pendingInvites} (${pendingUsers.length})`}
                />
              </ModalHeader>

              <ModalBody>
                {loadingPendingUsers ? (
                  <div style={{textAlign: 'center', padding: '40px'}}>
                    <TextBodyMedium text={t.common.loadingPendingInvites} />
                  </div>
                ) : pendingUsers.length === 0 ? (
                  <div style={{textAlign: 'center', padding: '40px'}}>
                    <TextBodyMedium text={t.common.noPendingInvitesFound} />
                  </div>
                ) : (
                  <div>
                    <TextBodySmall
                      text={t.common.theseTeamMembersInvited}
                      color={Palette.almostBlack500}
                    />
                    <PaddingBottom amount={16} />

                    {pendingUsers.map((user, index) => (
                      <InviteItem key={user.email || index}>
                        <div>
                          <TextHeading5 text={user.email} />
                          <TextBodySmall
                            text={`${t.common.invited} ${
                              user.invitedAt
                                ? new Date(
                                    typeof user.invitedAt === 'string'
                                      ? user.invitedAt
                                      : (user.invitedAt as any).seconds * 1000,
                                  ).toLocaleDateString()
                                : t.common.recently
                            }`}
                            color={Palette.almostBlack500}
                          />
                        </div>
                        <InviteItemActions>
                          <PrimaryButton
                            label={
                              reInviteLoading === user.email
                                ? t.common.sending
                                : t.common.reInvite
                            }
                            onClick={() => handleReInvite(user)}
                            isDisabled={reInviteLoading === user.email}
                            customWidth={100}
                          />
                          <IconButton
                            backgroundColor={Palette.primarySubtleGreen}
                            onClick={() => handleDeleteInvite(user.email)}
                            icon={<AssetIconTrash />}
                          />
                        </InviteItemActions>
                      </InviteItem>
                    ))}
                  </div>
                )}
              </ModalBody>

              <ModalFooter>
                <SecondaryButton
                  label={t.common.close}
                  onClick={() => setShowPendingModal(false)}
                />
              </ModalFooter>
            </ModalContent>
          </ModalOverlay>
        )}
      </AnimatePresence>

      {userDetails?.companyId && (
        <InviteUsersModal
          isOpen={showInviteModal}
          onClose={() => setShowInviteModal(false)}
          companyId={userDetails.companyId}
        />
      )}

      <UserListModal
        isOpen={showCalendarModal}
        onClose={() => setShowCalendarModal(false)}
        title={t.calendar.calendarIntegration}
        users={(employees || []) as User[]}
        showOrganizationalInfo={true}
        showCalendarIntegration={true}
      />
    </>
  );
});
